import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Play, X, Clock, Repeat, Activity, Target, ChevronDown, ChevronUp,
  Flame, Dumbbell, Award, ArrowRight, CheckCircle2, Heart, ArrowLeft,
} from "lucide-react";
import {
  TRAINEE, PROGRAM_INFO, POSTURE_PROBLEMS, PROGRAM_GOALS,
  WARMUP, POSTURE_DRILLS, WEEKLY_PROGRAM, COOLDOWN, TIPS,
} from "./data";

export default function GoodPT() {
  const [activeDay, setActiveDay] = useState(0);
  const [video, setVideo] = useState(null);

  const todayIdx = (() => {
    const d = new Date().getDay(); // 0=Sun..6=Sat
    if (d >= 1 && d <= 5) return d - 1; // Mon-Fri → 0..4
    return 0;
  })();

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="goodpt-page">
      {/* TOP NAV */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="container-x flex items-center justify-between h-14">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm font-display uppercase tracking-wider" data-testid="goodpt-back">
            <ArrowLeft className="h-4 w-4" /> Anasayfa
          </Link>
          <div className="flex items-center gap-2">
            <img src="/assets/fcs-logo.png" alt="FCS" className="h-9 w-9" />
            <span className="font-display font-black uppercase tracking-wider">GoodPT</span>
          </div>
          <div className="w-20" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-24 -right-24 h-[360px] w-[360px] rounded-full bg-amber-500/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-[320px] w-[320px] rounded-full bg-primary/15 blur-3xl" />

        <div className="container-x relative py-8 md:py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 ring-1 ring-amber-500/30 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-soft" />
            <span className="text-[10px] font-display font-bold uppercase tracking-[0.25em] text-amber-300">Goodlife FCS · Kişisel Program</span>
          </div>

          <div className="text-xs font-display uppercase tracking-[0.3em] text-muted-foreground mb-3">Hazırlanan Sporcu</div>
          <h1 className="font-brand uppercase leading-[0.9]" style={{ fontSize: "clamp(2.5rem, 9vw, 5rem)" }}>
            {TRAINEE.name}
          </h1>
          <div className="mt-3 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <span className="font-display text-lg md:text-xl uppercase tracking-wider text-primary font-black">V-Shape</span>
            <span className="text-muted-foreground">&</span>
            <span className="font-display text-lg md:text-xl uppercase tracking-wider font-black">Postür Düzeltme</span>
          </div>
          <div className="mt-1 text-xs font-display uppercase tracking-[0.25em] text-muted-foreground">{TRAINEE.phase}</div>

          <blockquote className="mt-6 max-w-xl mx-auto text-sm md:text-base italic text-muted-foreground border-l-2 border-amber-400/60 pl-4 text-left sm:text-center sm:border-l-0 sm:pl-0">
            &quot;{TRAINEE.motto}&quot;
          </blockquote>
        </div>
      </section>

      {/* PROGRAM INFO PILLS */}
      <section className="py-6 border-b border-border bg-[hsl(218_55%_5%)]">
        <div className="container-x">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {PROGRAM_INFO.map((p) => (
              <div key={p.label} className="card-elev p-4 text-center">
                <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">{p.label}</div>
                <div className="mt-1 font-display text-2xl font-black text-primary">{p.value}</div>
                <div className="text-xs text-muted-foreground">{p.sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 flex justify-center">
            <Link
              to="/goodpt/beslenme"
              data-testid="cta-nutrition"
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white shadow-[0_10px_30px_-6px_rgba(16,185,129,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              🥗 Beslenme & Supplement Planı
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* POSTURE STRIP */}
      <section className="py-10 border-b border-border">
        <div className="container-x">
          <div className="text-center mb-7">
            <div className="eyebrow justify-center mb-2">Postür Fotoğrafları</div>
            <h2 className="heading-md">Umut&apos;un Mevcut Duruşu</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:gap-5 max-w-3xl mx-auto" data-testid="posture-strip">
            {[
              { src: "/assets/umut-4.jpg", label: "Önden" },
              { src: "/assets/umut-2.jpg", label: "Yandan" },
              { src: "/assets/umut-1.jpg", label: "Arkadan" },
            ].map((p) => (
              <figure key={p.label} className="relative group">
                <div className="aspect-[3/4] overflow-hidden rounded-lg border border-border bg-card ring-1 ring-primary/20 shadow-[0_8px_24px_-8px_hsl(202_100%_50%/0.25)]">
                  <img
                    src={p.src}
                    alt={`Umut Şahin — ${p.label} postür`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <figcaption className="mt-2 text-center font-display text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] text-muted-foreground">
                  {p.label}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* YORUMLARIMIZ — observations */}
      <section className="section-pad" data-testid="observations">
        <div className="container-x">
          <SectionTitle
            eyebrow="01 · Yorumlarımız"
            title="Fotoğraflarda Ne Görüyoruz?"
            subtitle="Postür analizinde tespit ettiğimiz mevcut durum."
          />
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {POSTURE_PROBLEMS.map((p, i) => (
              <div key={i} className="card-elev p-4 flex items-start gap-3">
                <div className="h-7 w-7 shrink-0 rounded-md bg-red-500/15 ring-1 ring-red-500/30 flex items-center justify-center text-red-400 text-xs font-display font-black">
                  {i + 1}
                </div>
                <span className="text-sm text-muted-foreground leading-relaxed">{p.problem}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ÇÖZÜM ÖNERİMİZ — solution */}
      <section className="section-pad border-y border-border bg-[hsl(218_55%_5%)]" data-testid="solution">
        <div className="container-x">
          <SectionTitle
            eyebrow="02 · Çözüm Önerimiz"
            title="Hedefimiz & Yaklaşımımız"
            subtitle="Her problem için hedef belirledik ve sonuca götürecek program tasarımını oluşturduk."
          />

          <div className="max-w-3xl mx-auto space-y-3 mb-10">
            {POSTURE_PROBLEMS.map((p, i) => (
              <div key={i} className="card-elev p-4 grid grid-cols-[auto_1fr_auto_1fr] gap-3 items-center text-sm">
                <span className="text-[10px] font-display font-black text-muted-foreground tracking-widest w-5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-muted-foreground line-through opacity-70">{p.problem}</span>
                <ArrowRight className="h-4 w-4 text-primary shrink-0" />
                <span className="font-display font-bold uppercase tracking-wider text-emerald-400 text-xs sm:text-sm">{p.target}</span>
              </div>
            ))}
          </div>

          <div className="text-center mb-5">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.22em] text-muted-foreground">Bu programla kazanacakların</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto">
            {PROGRAM_GOALS.map((g, i) => (
              <div key={i} className="card-elev p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-sm">{g}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANIMIZ — the plan starts */}
      <section className="pt-12 pb-2">
        <div className="container-x text-center">
          <div className="eyebrow justify-center mb-3">03 · Planımız</div>
          <h2 className="heading-lg">Antrenman Programı</h2>
          <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Aşağıda 4 haftalık ilk faz için günlük antrenman planın yer alıyor.
            Her hareketin üzerine tıklayarak nasıl yapıldığını izleyebilirsin.
          </p>
        </div>
      </section>

      {/* PRE-WORKOUT */}
      <section className="section-pad">
        <div className="container-x">
          <SectionTitle eyebrow="Her Antrenman Öncesi" title="Isınma + Postür Düzeltme" subtitle="Her antrenmandan önce 10-12 dakikalık hazırlık. Asla atlama." />

          <div className="grid lg:grid-cols-2 gap-6">
            <ExerciseGroup
              label="Isınma (5 dakika)"
              icon={Flame}
              accent="amber"
              exercises={WARMUP}
              onPlay={setVideo}
              prefix="warmup"
            />
            <ExerciseGroup
              label="Postür Düzeltme (5-7 dakika)"
              icon={Target}
              accent="primary"
              exercises={POSTURE_DRILLS}
              onPlay={setVideo}
              prefix="posture"
            />
          </div>
        </div>
      </section>

      {/* WEEKLY PROGRAM */}
      <section className="section-pad border-y border-border bg-[hsl(218_55%_5%)]" data-testid="weekly-program">
        <div className="container-x">
          <SectionTitle eyebrow="Haftalık Program" title="5 Günlük Antrenman Planı" />

          {/* Day tabs — minimal 5-col grid, no scroll */}
          <div className="grid grid-cols-5 gap-1.5 sm:gap-2 mb-6" data-testid="day-tabs">
            {WEEKLY_PROGRAM.map((d, i) => {
              const isActive = i === activeDay;
              const isToday = i === todayIdx;
              const shortDay = d.day.slice(0, 3).toUpperCase();
              return (
                <button
                  key={d.day}
                  type="button"
                  onClick={() => setActiveDay(i)}
                  data-testid={`day-tab-${i}`}
                  className={`relative rounded-md border py-2.5 px-1 text-center transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground border-primary shadow-[0_6px_18px_-6px_hsl(202_100%_50%/0.4)]"
                      : "border-border bg-card hover:border-primary"
                  }`}
                >
                  <div className="font-display font-black uppercase tracking-wider text-sm sm:text-base">{shortDay}</div>
                  {isToday && (
                    <span className="absolute -top-1 -right-1 inline-flex h-2 w-2 rounded-full bg-amber-400 ring-2 ring-background pulse-soft" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Active day content */}
          <div className={`card-elev p-5 md:p-8 bg-gradient-to-br ${WEEKLY_PROGRAM[activeDay].color}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <div className="eyebrow mb-1.5">{WEEKLY_PROGRAM[activeDay].day}</div>
                <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight">{WEEKLY_PROGRAM[activeDay].focus}</h3>
              </div>
              {WEEKLY_PROGRAM[activeDay].badge && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 ring-1 ring-amber-500/40 text-[10px] font-display font-black uppercase tracking-[0.2em] text-amber-300">
                  <Award className="h-3 w-3" /> {WEEKLY_PROGRAM[activeDay].badge}
                </span>
              )}
            </div>

            <div className="space-y-2.5">
              {WEEKLY_PROGRAM[activeDay].exercises.map((ex, i) => (
                <ExerciseRow key={i} index={i + 1} exercise={ex} onPlay={() => setVideo(ex)} testid={`day-${activeDay}-ex-${i}`} />
              ))}
            </div>

            {WEEKLY_PROGRAM[activeDay].cardio && (
              <div className="mt-5 p-4 rounded-md border border-amber-500/40 bg-amber-500/5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-amber-500/20 ring-1 ring-amber-500/40 flex items-center justify-center text-amber-300">
                  <Heart className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] font-display uppercase tracking-[0.22em] text-amber-300">Kardiyo</div>
                  <div className="font-display font-bold">{WEEKLY_PROGRAM[activeDay].cardio}</div>
                </div>
              </div>
            )}

            <div className="mt-6 grid sm:grid-cols-3 gap-3 pt-5 border-t border-border">
              <Stat icon={Repeat} label="Set × Tekrar" value="3 × 12" />
              <Stat icon={Clock} label="Dinlenme" value="60-90 sn" />
              <Stat icon={Activity} label="Tempo" value="Kontrollü" />
            </div>
          </div>
        </div>
      </section>

      {/* COOLDOWN */}
      <section className="section-pad">
        <div className="container-x">
          <SectionTitle eyebrow="Antrenman Sonrası" title="Soğuma & Esneme (5 dakika)" subtitle="Antrenman sonrasında esneme — postür için kritik." />
          <div className="grid sm:grid-cols-3 gap-3">
            {COOLDOWN.map((ex, i) => (
              <ExerciseRow key={i} index={i + 1} exercise={ex} onPlay={() => setVideo(ex)} testid={`cooldown-${i}`} compact />
            ))}
          </div>
        </div>
      </section>

      {/* TIPS */}
      <section className="border-t border-border bg-[hsl(218_55%_5%)] py-12">
        <div className="container-x">
          <SectionTitle eyebrow="Ek Bilgiler" title="Başarı İçin Beş Altın Kural" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TIPS.map((t, i) => (
              <div key={i} className="card-elev p-5">
                <div className="text-3xl mb-3">{t.icon}</div>
                <h4 className="font-display font-bold uppercase tracking-wider text-base">{t.title}</h4>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-background py-8">
        <div className="container-x text-center">
          <img src="/assets/fcs-logo.png" alt="FCS" className="h-12 w-12 mx-auto mb-3" />
          <div className="font-display font-black uppercase tracking-wider">Focus Spor Kulübü</div>
          <div className="text-xs text-muted-foreground mt-1">Goodlife FCS · Kişisel Antrenman Programı</div>
          <div className="text-xs text-muted-foreground mt-3">Sorularını antrenörün ile paylaş.</div>
        </div>
      </footer>

      {/* VIDEO MODAL */}
      {video && <VideoModal video={video} onClose={() => setVideo(null)} />}
    </div>
  );
}

function SectionTitle({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-7 text-center">
      <div className="eyebrow mb-2 justify-center">{eyebrow}</div>
      <h2 className="heading-lg">{title}</h2>
      {subtitle && <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function ExerciseGroup({ label, icon: Icon, accent, exercises, onPlay, prefix }) {
  const accentClass = accent === "amber" ? "text-amber-300 bg-amber-500/15 ring-amber-500/30" : "text-primary bg-primary/15 ring-primary/30";
  return (
    <div className="card-elev p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className={`h-10 w-10 rounded-md ring-1 ${accentClass} flex items-center justify-center`}>
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="font-display font-bold uppercase tracking-wider">{label}</h3>
      </div>
      <div className="space-y-2">
        {exercises.map((ex, i) => (
          <ExerciseRow key={i} index={i + 1} exercise={ex} onPlay={() => onPlay(ex)} testid={`${prefix}-${i}`} compact />
        ))}
      </div>
    </div>
  );
}

function ExerciseRow({ index, exercise, onPlay, testid, compact }) {
  return (
    <button
      type="button"
      onClick={onPlay}
      data-testid={testid}
      className="w-full group flex items-center gap-3 p-3 rounded-md border border-border bg-card/50 hover:bg-card hover:border-primary transition-all text-left"
    >
      <div className="font-display text-xl font-black text-primary w-7 text-center">{index}</div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold uppercase tracking-wider text-sm md:text-base truncate">{exercise.name}</div>
        <div className="text-xs text-muted-foreground">{exercise.reps}</div>
      </div>
      <div className="h-10 w-10 shrink-0 rounded-full bg-primary/15 ring-1 ring-primary/30 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        <Play className="h-4 w-4" fill="currentColor" />
      </div>
    </button>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-md bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-primary shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
        <div className="font-display font-bold">{value}</div>
      </div>
    </div>
  );
}

function VideoModal({ video, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] bg-background/95 backdrop-blur-md p-4 sm:p-8 flex items-start sm:items-center justify-center overflow-y-auto"
      onClick={onClose}
      data-testid="video-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-3xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">Hareket</div>
            <h3 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight">{video.name}</h3>
            <div className="text-sm text-primary mt-1">{video.reps}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-11 w-11 rounded-md border border-border bg-card hover:border-primary hover:text-primary"
            data-testid="video-close"
          >
            <X className="h-5 w-5 mx-auto" />
          </button>
        </div>

        <div className="aspect-video w-full rounded-lg overflow-hidden border border-border bg-black">
          <iframe
            title={video.name}
            src={`https://www.youtube.com/embed/${video.video}?autoplay=1&modestbranding=1&rel=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="mt-4 flex flex-wrap justify-between gap-3">
          <a
            href={`https://www.youtube.com/watch?v=${video.video}`}
            target="_blank" rel="noreferrer"
            className="btn-secondary text-xs px-4 py-2"
          >
            YouTube&apos;da Aç
          </a>
          <button type="button" onClick={onClose} className="btn-primary text-xs px-4 py-2">
            Anladım, Yapacağım
          </button>
        </div>
      </div>
    </div>
  );
}
